import React, { useEffect, useState, useRef, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { ReactComponent as ClientsIcon } from "../../images/clients.svg";
import { ReactComponent as RoleIcon } from "../../images/Role.svg";
import { ReactComponent as ActivitiesIcon } from "../../images/activities.svg";
import { ReactComponent as MealServicesIcon } from "../../images/meal-services.svg";
import { ReactComponent as DietIcon } from "../../images/diet.svg";
import { ReactComponent as RecipientIcon } from "../../images/recipient.svg";
import { ReactComponent as OccasionIcon } from "../../images/occasion.svg";
import { ReactComponent as AllergenIcon } from "../../images/allergen.svg";
import { ReactComponent as FsMastersIcon } from "../../images/fs-masters.svg";
import { ReactComponent as HelpManualIcon } from "../../images/help-manual.svg";
import api from "../../../src/api_interceptor/api";
import Loader from "../../components/loader/Loader";

const SIDEBAR_ICON_CLASS = "sidebar-nav-icon w-5 h-5 shrink-0";

const ICON_MAP = {
  Clients: ClientsIcon,
  Roles: RoleIcon,
  Activities: ActivitiesIcon,
  "Meal Service": MealServicesIcon,
  "Meal Services": MealServicesIcon,
  Diet: DietIcon,
  Diets: DietIcon,
  Recipient: RecipientIcon,
  Recipients: RecipientIcon,
  Occasion: OccasionIcon,
  Occasions: OccasionIcon,
  Allergen: AllergenIcon,
  Allergens: AllergenIcon,
  "FS Masters": FsMastersIcon,
  "Help Manual": HelpManualIcon, // Placeholder icon for Help Manual
};

const FS_CHILD_PATHS = [
  "/diet-master",
  "/recipient-master",
  "/occasion-master",
  "/meal-services",
  "/allergen-master",
];

const FS_CHILD_ORDER = {
  "/diet-master": 1,
  "/recipient-master": 2,
  "/occasion-master": 3,
  "/meal-services": 4,
  "/allergen-master": 5,
};

const FS_DISPLAY_NAMES = {
  "/diet-master": "Diets",
  "/recipient-master": "Recipients",
  "/occasion-master": "Occasions",
  "/meal-services": "Meal Services",
  "/allergen-master": "Allergens",
};

const isGroupHeading = (module) => {
  const path = (module.ModulePath || "").trim();
  return path === "/fs-masters" || path === "" || path === "/";
};

const isPathActive = (pathname, modulePath) => {
  if (!modulePath) return false;
  return pathname === modulePath || pathname.startsWith(`${modulePath}/`);
};

const HELP_MANUAL_MODULE = {
  Id: -9002,
  ModuleName: "Help Manual",
  ModulePath: "/helpmanual",
  ParentModuleId: null,
  GroupIndex: 60,
};

/** Normalize API modules so FS masters always nest under FS Masters. */
const normalizeModulesForNav = (rawModules) => {
  const modules = (Array.isArray(rawModules) ? rawModules : []).map((m) => ({
    ...m,
  }));

  modules.forEach((m) => {
    const path = (m.ModulePath || "").trim();
    if (path === "/help-manual" || path === "/helpmanual") {
      m.ModulePath = "/helpmanual";
      m.ModuleName = "Help Manual";
    }
  });

  const hasHelpManual = modules.some(
    (m) => (m.ModulePath || "").trim() === "/helpmanual"
  );
  if (!hasHelpManual) {
    modules.push({ ...HELP_MANUAL_MODULE });
  }

  let parent = modules.find((m) => (m.ModulePath || "").trim() === "/fs-masters");
  if (!parent) {
    parent = {
      Id: -9001,
      ModuleName: "FS Masters",
      ModulePath: "/fs-masters",
      ParentModuleId: null,
      GroupIndex: 50,
    };
    const hasFsChild = modules.some((m) =>
      FS_CHILD_PATHS.includes((m.ModulePath || "").trim())
    );
    if (hasFsChild) {
      modules.push(parent);
    }
  }

  modules.forEach((m) => {
    const path = (m.ModulePath || "").trim();
    if (!FS_CHILD_PATHS.includes(path)) return;
    m.ParentModuleId = parent.Id;
    m.ModuleName = FS_DISPLAY_NAMES[path] || m.ModuleName;
    m.GroupIndex = FS_CHILD_ORDER[path] ?? m.GroupIndex;
  });

  return modules;
};

const Sidebar = ({ isOpen }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [moduleList, setModuleList] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const location = useLocation();
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;

    hasFetched.current = true;
    getModuleList();
  }, []);

  const getModuleList = () => {
    setIsLoading(true);
    const API_URL = "api/GetSuperAdminModuleList";
    const req_body = {};
    api
      .post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response && response.data) {
          setModuleList(response.data);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
      });
  };

  const navTree = useMemo(() => {
    const modules = normalizeModulesForNav(moduleList);
    const byId = new Map(modules.map((m) => [m.Id, m]));
    const childrenByParent = new Map();

    modules.forEach((m) => {
      if (!m.ParentModuleId) return;
      if (!childrenByParent.has(m.ParentModuleId)) {
        childrenByParent.set(m.ParentModuleId, []);
      }
      childrenByParent.get(m.ParentModuleId).push(m);
    });

    childrenByParent.forEach((children) => {
      children.sort(
        (a, b) =>
          (a.GroupIndex ?? 99) - (b.GroupIndex ?? 99) ||
          String(a.ModuleName || "").localeCompare(String(b.ModuleName || ""))
      );
    });

    const topLevel = modules.filter((m) => !m.ParentModuleId);

    return topLevel
      .map((parent) => ({
        parent,
        children: childrenByParent.get(parent.Id) || [],
      }))
      .filter(({ parent, children }) => {
        if (isGroupHeading(parent)) {
          return children.length > 0;
        }
        return true;
      })
      .concat(
        // Orphan children whose parent is missing from the list
        [...childrenByParent.entries()]
          .filter(([parentId]) => !byId.has(parentId))
          .flatMap(([, children]) =>
            children.map((child) => ({ parent: child, children: [] }))
          )
      );
  }, [moduleList]);

  useEffect(() => {
    const next = {};
    navTree.forEach(({ parent, children }) => {
      if (children.length === 0) return;
      const childActive = children.some((c) =>
        isPathActive(location.pathname, c.ModulePath)
      );
      if (childActive) {
        next[parent.Id] = true;
      }
    });
    setExpandedGroups((prev) => ({ ...prev, ...next }));
  }, [location.pathname, navTree]);

  const toggleGroup = (id) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const renderLeaf = (module, nested) => {
    const Icon = ICON_MAP[module.ModuleName];
    return (
      <NavLink
        key={module.Id}
        title={module.ModuleName}
        to={module.ModulePath}
        className={({ isActive }) =>
          `flex items-center gap-2 px-4 py-2 min-h-[44px] transition-colors ${
            nested ? "pl-10 text-sm" : ""
          } ${
            isActive
              ? "bg-primary text-white"
              : "text-gray-700 hover:bg-gray-100"
          }`
        }
      >
        {Icon && <Icon className={SIDEBAR_ICON_CLASS} />}
        {module.ModuleName}
      </NavLink>
    );
  };

  return (
    <>
      {isLoading && <Loader />}
      <aside
        className={`
        bg-white border-r shrink-0
        transition-all duration-300 ease-in-out
        overflow-hidden
    ${isOpen ? "max-w-[224px] opacity-100" : "max-w-0 opacity-0"}
      `}
      >
        <nav className="w-56 py-4 space-y-1">
          {navTree.map(({ parent, children }) => {
            if (children.length === 0) {
              return renderLeaf(parent, false);
            }

            const expanded = !!expandedGroups[parent.Id];
            const ParentIcon = ICON_MAP[parent.ModuleName];
            const parentActive = children.some((c) =>
              isPathActive(location.pathname, c.ModulePath)
            );

            return (
              <div key={parent.Id}>
                <button
                  type="button"
                  title={parent.ModuleName}
                  onClick={() => toggleGroup(parent.Id)}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-left transition-colors ${
                    parentActive
                      ? "text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {ParentIcon && <ParentIcon className={SIDEBAR_ICON_CLASS} />}
                  <span className="flex-1 truncate">{parent.ModuleName}</span>
                  {expanded ? (
                    <FiChevronDown className="w-4 h-4 shrink-0" />
                  ) : (
                    <FiChevronRight className="w-4 h-4 shrink-0" />
                  )}
                </button>
                {expanded && children.map((child) => renderLeaf(child, true))}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
